import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { SiteStat, Integration, CaptureDevice, TeamCoverage } from '../lib/sources';
import { hasActiveBackfill } from '../lib/sources';

export type { SiteStat, Integration, CaptureDevice, TeamCoverage };

export const sourcesSurfaceQueryKey = ['surfaces', 'sources'] as const;

export interface SourcesSurfaceData {
  sites: SiteStat[];
  integrations: Integration[];
  has_api_key?: boolean;
  capture_paused?: boolean;
  // Connected Claude Code devices (active terum-capture API keys) for the Devices drawer.
  devices?: CaptureDevice[];
  // Ghost Phase B (§3.2): team coverage strip data. Null/absent when the caller has no team.
  coverage?: TeamCoverage | null;
}

// latent #4 + spec §7: while any source is mid-import, poll the BFF so completion + partial counts
// sync to the card on a timer — instead of depending on a single extension postMessage a killed MV3
// service worker may never deliver (the "spinner that never ends / status reverts" class). "Mid-import"
// is now the UNIFORM server signal capture_status==='syncing' (covers OAuth/github backfills too, not
// just chat), with the legacy chat backfill signal kept as a fallback. Fast (~4s) only while syncing;
// stops on the server TERMINAL signal (state leaves 'syncing') — never a fixed client timeout that
// gives up early. The server's staleness window guarantees the poll ends even if a worker dies.
const SYNCING_POLL_MS = 4_000;

function anySyncing(integrations: Integration[]): boolean {
  return hasActiveBackfill(integrations) || integrations.some((i) => i.capture_status?.state === 'syncing');
}

export function useSourcesSurface() {
  const query = useQuery({
    queryKey: sourcesSurfaceQueryKey,
    queryFn: () => apiFetch<SourcesSurfaceData>('/api/surfaces/sources'),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Refetch when the tab regains focus (the global QueryClient default is off) so a source
    // connected out-of-band — the OAuth popup/redirect flow — reflects on return, backstopping the
    // per-mutation invalidateQueries. Respects the 2-min staleTime, so it's not an every-focus hit.
    refetchOnWindowFocus: true,
    // Poll only while some source reports mid-import; stop once all runs are terminal.
    refetchInterval: (query) =>
      anySyncing(query.state.data?.integrations ?? []) ? SYNCING_POLL_MS : false,
  });
  return { ...query, data: query.data ?? undefined };
}
